import { InsertEmoticon, Send } from "@mui/icons-material";
import styles from "./message-bar.module.css";
import { useChatStore } from "../../../../../../../lib/chat-store/chats-store";
import { StoredChat } from "../../../../../../../lib/chat-store/types";
import { useEffect, useRef, useState } from "react";
import { IoIosAttach } from "react-icons/io";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { IoDocuments, IoHome, IoImages, IoVideocam } from "react-icons/io5";
import { PiHouse, PiHouseFill } from "react-icons/pi";
import { handleFilesInput, useUploadStore } from "../../../upload-store";
import { useTranslations } from "next-intl";

export default function MessageBar({ chatData }: { chatData: StoredChat }) {
  const t = useTranslations("ChatsView");
  const { sendMessage } = useChatStore();
  const [message, setMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [showAttach, setShowAttach] = useState(false);
  const attachRef = useRef<HTMLDivElement>(null);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  const { uploads, addFiles } = useUploadStore();

  // 🔥 input refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // 🔥 handler
  const onFilesSelected = (files: File[]) => {
    handleFilesInput(files, uploads, addFiles);
    setShowAttach(false); // close menu
  };

  const handleSend = () => {
    if (!message.trim()) return;
    sendMessage(
      chatData.conversation_id,
      "text",
      { text: message.trim(), timezone: timezone },
      chatData.receiver_id,
    );
    setMessage("");
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        !emojiRef.current?.contains(e.target as Node) &&
        !emojiButtonRef.current?.contains(e.target as Node)
      ) {
        setShowEmoji(false);
      }

      if (
        !attachRef.current?.contains(e.target as Node) &&
        !attachButtonRef.current?.contains(e.target as Node)
      ) {
        setShowAttach(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onEmojiClick = (emoji: EmojiClickData) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart ?? message.length;
    const end = input.selectionEnd ?? message.length;

    const newText =
      message.substring(0, start) + emoji.emoji + message.substring(end);

    setMessage(newText);

    // Restore caret AFTER React updates value
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + emoji.emoji.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className={styles.messageBarWrapper}>
      <button
        className={`${styles.roundButton} ${styles.emoticonButton}`}
        onClick={() => setShowEmoji((v) => !v)}
        ref={emojiButtonRef}
      >
        <InsertEmoticon className={styles.emoticonIcon} />
      </button>
      {showEmoji && (
        <div ref={emojiRef} className={styles.emojiWrapper}>
          <EmojiPicker
            onEmojiClick={onEmojiClick}
            className={styles.myEmojiPicker}
            width={"100%"}
            height={"100%"}
            style={
              {
                borderRadius: "14px",
                boxShadow: "0 10px 40px rgba(0,0,0,.4)",
                overflow: "hidden",

                // CSS VARIABLES
                "--epr-bg-color": "#2b2f36",
                "--epr-category-label-bg-color": "#2b2f36",
                "--epr-hover-bg-color": "rgba(255,255,255,.08)",
                "--epr-text-color": "white",
                "--epr-search-input-bg-color": "#1f2228",
                "--epr-search-border-color": "transparent",
                "--epr-picker-border-color": "transparent",
              } as React.CSSProperties
            }
          />
        </div>
      )}
      <button
        ref={attachButtonRef}
        onClick={() => setShowAttach((v) => !v)}
        className={`${styles.roundButton} ${styles.attachButton}`}
      >
        <IoIosAttach className={styles.emoticonIcon} />
      </button>
      {showAttach && (
        <div ref={attachRef} className={styles.attachWrapper}>
          <button
            className={styles.attachOption}
            onClick={() => {
              photoInputRef.current?.click();
              setShowAttach(false);
            }}
          >
            <span className={styles.icon}>
              <IoImages />
            </span>
            <span className={styles.text}>{t("photos")}</span>
          </button>

          <button
            className={styles.attachOption}
            onClick={() => {
              videoInputRef.current?.click();
              setShowAttach(false);
            }}
          >
            <span className={styles.icon}>
              <IoVideocam />
            </span>
            <span className={styles.text}>{t("videos")}</span>
          </button>

          <button
            className={styles.attachOption}
            onClick={() => {
              docInputRef.current?.click();
              setShowAttach(false);
            }}
          >
            <span className={styles.icon}>
              <IoDocuments />
            </span>
            <span className={styles.text}>{t("documents")}</span>
          </button>

          {/* <button className={styles.attachOption}>
            <span className={styles.icon}>
              <PiHouseFill />
            </span>
            <span className={styles.text}>Listings</span>
          </button> */}
        </div>
      )}
      <input
        ref={inputRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className={styles.input}
        type="text"
        placeholder="Message..."
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSend();
        }}
      />
      <button
        onClick={handleSend}
        className={`${styles.roundButton} ${styles.sendButton}`}
        disabled={!message.trim()}
      >
        <span className={styles.sendText}>{t("send")}</span>
        <Send />
      </button>

      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          onFilesSelected(files);
          e.target.value = "";
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          onFilesSelected(files);
          e.target.value = "";
        }}
      />
      <input
        ref={docInputRef}
        type="file"
        accept=".pdf,.docx,.xlsx"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          onFilesSelected(files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
